import {
  all, put, takeEvery, select, takeLatest, fork, join, call,
} from 'redux-saga/effects';
import { show as showNotification } from 'circe/notifications/actions';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import * as I18n from 'circe/I18n';
import moment from 'moment';
import * as api from 'domain/operations/drivers/itineraryStatus/view/api';
import {
  fetch,
  fetched,
  shipmentAttached,
  shipmentDeattached,
  shipmentRearranged,
  markAsCollected,
  markedAsCollected,
  confirmCashCollected,
  confirmedCashCollected,
  markAsReceived,
  markedAsReceived,
  attachToItinerary,
  deattachFromItinerary,
  rearrangeShipments,
  updateRecipient,
  updatedRecipient,
} from './actions';
import { getSummary, getShipments } from './selectors';

function* doMarkAsCollected(action) {
  const { payload } = action;
  const { itineraryId } = payload;

  const state = yield select();

  const headers = getCurrentAuthHeader(state);
  const summary = getSummary(state);
  const shipments = getShipments(state);

  yield call(
    api.complete,
    itineraryId,
    {
      codCollected: {
        amount: summary.totalCod.amount,
        currency: summary.totalCod.currency,
      },
      returnedShipmentIds: shipments
        .filter((shipment) => shipment.received === true)
        .map((shipment) => shipment.shipmentId),
    },
    headers,
  );

  yield put(markedAsCollected(itineraryId));
}

function* doConfirmCashCollected(action) {
  const { payload } = action;
  const { itineraryId, confirmedCash } = payload;

  try {
    const state = yield select();

    const headers = getCurrentAuthHeader(state);
    const summary = getSummary(state);
    const shipments = getShipments(state);

    yield call(
      api.confirmCash,
      itineraryId,
      {
        codCollected: {
          amount: confirmedCash,
          currency: summary.totalCod.currency,
        },
        shipmentIds: shipments
          .filter((shipment) => shipment.status === 'DELIVERED' && shipment.pendingCodDepositing)
          .map((shipment) => shipment.shipmentId),
      },
      headers,
    );
    yield put(
      showNotification(
        I18n.str('CODConfirmationSucceed', 'COD confirmed Successfully.'),
        'success',
      ),
    );
  } catch (error) {
    yield put(
      showNotification(I18n.str('CODConfirmationFailed', 'COD Confirmation Failed'), 'error'),
    );
  }

  yield put(confirmedCashCollected(itineraryId));
}

function* doMarkAsReceived(action) {
  const { payload } = action;
  const { itineraryId, shipmentIds } = payload;

  const state = yield select();

  const headers = getCurrentAuthHeader(state);

  yield call(
    api.confirmReceived,
    itineraryId,
    {
      returnedShipmentIds: shipmentIds,
    },
    headers,
  );
  yield put(markedAsReceived(shipmentIds));
}

function* doAttachToItinerary(context, action) {
  const { payload } = action;
  const { itineraryId, shipmentIds } = payload;

  const state = yield select();

  const headers = getCurrentAuthHeader(state);
  try {
    for (const shipmentId of shipmentIds) {
      yield call(api.attachToItinerary, itineraryId, { shipmentId }, headers);
    }

    yield put(shipmentAttached());

    const itineraryUrl = context.history.location.pathname.split('/itineraryAttachment')[0];
    yield context.history.push(itineraryUrl);
    yield put(
      showNotification(
        I18n.str('shipmentAttachedSuccessfully', 'Shipment(s) Attached Successfully.'),
        'success',
      ),
    );
  } catch (error) {
    yield put(shipmentAttached());

    yield put(
      showNotification(
        I18n.str('unableToAttachShipmentToItinerary', 'Unable To Attach Shipment(s) To Itinerary.'),
        'error',
      ),
    );
  }
}

function* doDeattachFromItinerary(action) {
  const { payload } = action;
  const { itineraryId, shipmentId } = payload;

  const state = yield select();

  const headers = getCurrentAuthHeader(state);

  try {
    yield call(api.deattachFromItinerary, itineraryId, { shipmentId }, headers);

    yield put(shipmentDeattached());
    yield put(
      showNotification(
        I18n.str('shipmentDeattachedSuccessfully', 'Shipment(s) Deattached Successfully.'),
        'success',
      ),
    );
    location.reload();
  } catch (error) {
    yield put(shipmentDeattached());

    yield put(
      showNotification(
        I18n.str(
          'unableToDeattachShipmentFromItinerary',
          'Unable To Deattach Shipment(s) From Itinerary.',
        ),
        'error',
      ),
    );
  }
}

function* doRearrangeShipments(action) {
  const { payload } = action;
  const { itineraryId, from, to } = payload;

  const state = yield select();

  const headers = getCurrentAuthHeader(state);
  try {
    yield call(api.rearrangeShipments, itineraryId, { from, to }, headers);

    yield put(shipmentRearranged());

    yield put(
      showNotification(
        I18n.str('stopRearrangedSuccessfully', 'Stop Rearranged Successfully.'),
        'success',
      ),
    );
    location.reload();
  } catch (error) {
    yield put(shipmentRearranged());

    yield put(
      showNotification(I18n.str('unableToRearrangeShipment', 'Unable to rearrange stop'), 'error'),
    );
  }
}

function* doFetch(action) {
  const state = yield select();

  const { payload } = action;
  const { itineraryId } = payload;
  const headers = getCurrentAuthHeader(state);

  try {
    const resp = yield call(api.fetch, itineraryId, headers);
    resp.summary.driverName = resp.driverName;
    resp.summary.startTime = resp.startTime;
    resp.summary.closed = resp.closed;
    resp.summary.costSummary = resp.costSummary;
    resp.summary.invoiceData = resp.invoiceData;
    resp.summary.direction = resp.direction;
    resp.summary.carrierInfo = resp.carrierInfo;
    resp.summary.vehicle = resp.vehicle;
    yield put(fetched(resp));
  } catch (error) {
    yield put(
      showNotification(I18n.str('unableToFetchItinerary', 'Unable to fetch itinerary.'), 'error'),
    );
  }
}
function* doUpdateWaybill({ payload }) {
  try {
    const state = yield select();
    const headers = yield call(getCurrentAuthHeader, state);

    const { values } = payload;

    // if(values.status == "EXCEPTION"){ // TODO: buisness logic, big oppse
    //
    //   const data = {
    //     waybill: values.waybill,
    //     status: "UNASSIGNED",
    //     message: "",
    //   };
    //
    //   const resp = yield call(updateStatus, data, headers);
    //
    //   if(resp.responseCode == "SUCCESS"){
    //     throw new Error(resp.message);
    //   }
    // }

    const req = {
      salesOrderNumber: values.salesOrderNumber,
      salesOrderLineNumber: values.salesOrderLineNumber,
      itemNumber: values.itemNumber,
      dropoff: {
        address: values.toAddress[0].formatted_address,
        arrivalWindow: {
          begin: moment(values.deliveryDate)
            .add(3, 'hours')
            .toISOString(),
          end: moment(values.deliveryDate)
            .add(3, 'hours')
            .toISOString(),
          excludeBegin: true,
          excludeEnd: true,
        },
        coordinate: {
          latitude:
            typeof values.toAddress[0].geometry.location.lat === 'function'
              ? values.toAddress[0].geometry.location.lat()
              : values.toAddress[0].geometry.location.lat,
          longitude:
            typeof values.toAddress[0].geometry.location.lng === 'function'
              ? values.toAddress[0].geometry.location.lng()
              : values.toAddress[0].geometry.location.lng,
        },
        description: values.toAddress[0].formatted_address,
        duration: null,
      },
      receiverContact: {
        name: values.contact,
        phone: values.mobile,
        email: values.email,
      },
      shipmentsToAssign: [values.shipmentId],
    };
    const resp = yield call(api.updateRecipient, req, headers);

    if (!resp.success) {
      throw new Error(resp.message);
    }

    yield put(
      showNotification(
        `${I18n.str('waybillUpdatedSuccessfully', 'Waybill updated successfully.')}`,
        'success',
      ),
    );

    yield put(updatedRecipient());
  } catch (e) {
    console.error('Hellllllo', e);
    yield put(showNotification(I18n.str('waybillUpdateFailed', 'Waybill update failed.'), 'error'));
    // exception(e);
    yield put(updatedRecipient());
  }
}
function* watcher(context) {
  yield all([
    takeLatest(fetch().type, doFetch),
    takeLatest(markAsCollected().type, doMarkAsCollected),
    takeLatest(confirmCashCollected().type, doConfirmCashCollected),
    takeLatest(markAsReceived().type, doMarkAsReceived),
    takeLatest(attachToItinerary().type, doAttachToItinerary, context),
    takeLatest(deattachFromItinerary().type, doDeattachFromItinerary),
    takeLatest(rearrangeShipments().type, doRearrangeShipments),
    takeEvery(updateRecipient().type, doUpdateWaybill),
  ]);
}

export default watcher;
